// Sliding puzzle (the 15-puzzle and its 8 and 24 cousins): n by n squares, one empty; slide a tile into the gap
// until the picture, or the numbers 1 to n²-1, are in order. Pure functions over a plain state; the interface is
// sliding/ui.ts. Every shuffle here can be solved (half of all arrangements can't; the parity rule below).
import { seeded } from "../lib/rng"

export interface SlidingState {
  size: number
  /** Row-major: the tile number on each square (1..n²-1), 0 for the gap. Solved: 1, 2, …, 0 last. */
  tiles: number[]
  moves: number
  seed: string
  /** The picture's slug, or "numbers". */
  image: string
  attempt?: number
}
export const SIZES = [3, 4, 5] as const
export type Size = (typeof SIZES)[number]

export const solved = (n: number): number[] => Array.from({ length: n * n }, (_, i) => (i + 1) % (n * n))
export const isSolved = (s: SlidingState): boolean => s.tiles.every((v, i) => v === (i + 1) % s.tiles.length)
export const gap = (s: SlidingState): number => s.tiles.indexOf(0)

/** The parity rule: with the gap counted from the bottom row, an arrangement can be solved when inversions
 *  (pairs out of order) plus, on an even-sized board, the gap's row from the bottom (1-based) is... even. */
export function solvable(tiles: readonly number[], n: number): boolean {
  let inv = 0
  const t = tiles.filter((v) => v !== 0)
  for (let i = 0; i < t.length; i++) for (let j = i + 1; j < t.length; j++) if (t[i]! > t[j]!) inv++
  if (n % 2 === 1) return inv % 2 === 0
  const rowFromBottom = n - Math.floor(tiles.indexOf(0) / n)
  return (inv + rowFromBottom) % 2 === 1
}

/** A solvable shuffle for the seed: a random arrangement, fixed by swapping two tiles when its parity is wrong,
 *  and never the solved board. */
export function deal(seed: string, size: number = 4, image = "numbers"): SlidingState {
  const n = (SIZES as readonly number[]).includes(size) ? size : 4
  const rand = seeded(`sliding:${seed}:${n}`)
  const tiles = solved(n)
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[tiles[i], tiles[j]] = [tiles[j]!, tiles[i]!]
  }
  if (!solvable(tiles, n)) {
    const a = tiles.findIndex((v) => v !== 0)
    const b = tiles.findIndex((v, i) => v !== 0 && i !== a)
    ;[tiles[a], tiles[b]] = [tiles[b]!, tiles[a]!]
  }
  const s: SlidingState = { size: n, tiles, moves: 0, seed, image }
  // The solved board is a legal arrangement, but no puzzle: slide one tile, keeping parity.
  if (isSolved(s)) {
    const g = gap(s)
    const neighbour = g % n > 0 ? g - 1 : g + 1
    ;[tiles[g], tiles[neighbour]] = [tiles[neighbour]!, tiles[g]!]
  }
  return s
}

/** Slide the tile at square i (and any tiles between it and the gap in the same row or column). The same state
 *  when the tile isn't in line with the gap. */
export function slide(s: SlidingState, i: number): SlidingState {
  const n = s.size
  const g = gap(s)
  if (i === g || i < 0 || i >= n * n) return s
  const r = Math.floor(i / n), c = i % n, gr = Math.floor(g / n), gc = g % n
  if (r !== gr && c !== gc) return s
  const tiles = s.tiles.slice()
  const step = r === gr ? (c < gc ? -1 : 1) : (r < gr ? -n : n)
  // The gap walks toward the tile, one square at a time, carrying each tile back a square.
  let at = g
  while (at !== i) {
    const next = at + step
    tiles[at] = tiles[next]!
    tiles[next] = 0
    at = next
  }
  return { ...s, tiles, moves: s.moves + Math.abs(r === gr ? c - gc : r - gr) }
}

export type Dir = "left" | "right" | "up" | "down"
/** The square whose tile moves in direction `dir` into the gap (the tile on the gap's other side), or -1. */
export function tileFor(s: SlidingState, dir: Dir): number {
  const n = s.size
  const g = gap(s)
  const r = Math.floor(g / n), c = g % n
  if (dir === "left") return c + 1 < n ? g + 1 : -1
  if (dir === "right") return c > 0 ? g - 1 : -1
  if (dir === "up") return r + 1 < n ? g + n : -1
  return r > 0 ? g - n : -1
}

/** How far every tile is from home, added up. */
export function distance(s: SlidingState): number {
  const n = s.size
  let d = 0
  s.tiles.forEach((v, i) => {
    if (v === 0) return
    const home = v - 1
    d += Math.abs(Math.floor(i / n) - Math.floor(home / n)) + Math.abs((i % n) - (home % n))
  })
  return d
}

/** A tile to slide next: the single slide that leaves the tiles closest to home three slides on, avoiding the
 *  slide that just undoes the last one when another is as good. */
export function hint(s: SlidingState, last = -1): number {
  if (isSolved(s)) return -1
  const g = gap(s)
  const options = (st: SlidingState): number[] => (["left", "right", "up", "down"] as Dir[]).map((d) => tileFor(st, d)).filter((i) => i >= 0)
  const best = (st: SlidingState, depth: number, from: number): number => {
    if (isSolved(st)) return -1000 + depth
    if (depth === 0) return distance(st)
    let score = Infinity
    for (const i of options(st)) {
      if (i === from) continue
      score = Math.min(score, best(slide(st, i), depth - 1, gap(st)))
    }
    return score === Infinity ? distance(st) : score
  }
  let pick = -1, pickScore = Infinity
  for (const i of options(s)) {
    const next = slide(s, i)
    const score = best(next, 3, g) + (i === last ? 0.5 : 0)
    if (score < pickScore) { pickScore = score; pick = i }
  }
  return pick
}
